import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, ArrowLeft, Sparkles, Clock } from "lucide-react";
import { useTenant } from "../../contexts/TenantContext";
import { BrandButton } from "./TenantShell";
import { DocRow } from "./StepDocuments";
import { tapi, apiErr } from "../../lib/tenant";

export const ECO_DOCS = [
  { key: "pitch_deck", label: "Pitch deck", alt: [] },
  { key: "mgmt_accounts", label: "Audited financials or management accounts", alt: ["audited_accounts"] },
  { key: "financial_model", label: "Financial model", alt: [] },
  { key: "legal", label: "Company certificate", alt: [] },
];

// Estimated processing time: ~12s base + ~15s per document (figure extraction).
const estimateSeconds = (n) => Math.min(90, 12 + 15 * n);
const PHASES = (labels) => [
  "Checking your submission…",
  ...labels.map((l) => `Reading your ${l.toLowerCase()}…`),
  "Working out your Capital Readiness Score…",
  "Matching you with partners in the ecosystem…",
];

function SubmitProgress({ docLabels }) {
  const total = estimateSeconds(docLabels.length);
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => { const id = setInterval(() => setElapsed((e) => e + 1), 1000); return () => clearInterval(id); }, []);
  const remaining = Math.max(0, total - elapsed);
  const phases = PHASES(docLabels);
  const phase = phases[Math.min(phases.length - 1, Math.floor((elapsed / total) * phases.length))];
  const pct = Math.min(96, Math.round((elapsed / total) * 100));
  return (
    <div className="rounded-2xl border p-4" style={{ borderColor: "color-mix(in srgb, var(--tenant) 35%, white)", background: "color-mix(in srgb, var(--tenant) 6%, white)" }} data-testid="eco-submit-progress" aria-live="polite">
      <div className="flex items-center justify-between gap-3">
        <p className="flex items-center gap-2 text-sm font-bold" style={{ color: "var(--tenant)" }}><Loader2 size={15} className="animate-spin" /> <span data-testid="eco-submit-phase">{phase}</span></p>
        <p className="flex items-center gap-1.5 text-xs font-semibold text-neutral-600" data-testid="eco-submit-countdown">
          <Clock size={13} /> {remaining > 0 ? `About ${remaining}s left` : "Almost there — finalising…"}
        </p>
      </div>
      <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-neutral-200">
        <div className="h-full rounded-full transition-[width] duration-1000 ease-linear" style={{ width: `${remaining > 0 ? pct : 96}%`, background: "var(--tenant)" }} data-testid="eco-submit-bar" />
      </div>
      <p className="mt-2 text-[11px] text-neutral-500">Please keep this page open. We&apos;re reading your {docLabels.length || "submitted"} document{docLabels.length === 1 ? "" : "s"} and building your results.</p>
    </div>
  );
}

export const StepEcoDocuments = ({ documents, onChanged, onBack, onSubmitted, resubmit = false }) => {
  const { sub } = useTenant();
  const [busy, setBusy] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const filesFor = (req) => documents.filter((d) => d.doc_type === req.key || req.alt.includes(d.doc_type));

  const upload = async (req, files) => {
    if (!files.length) return;
    setBusy(req.key);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file); fd.append("doc_type", req.key);
      try {
        await tapi.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`${file.name} uploaded`);
      } catch (e) { toast.error(apiErr(e, `Couldn't upload ${file.name}`)); }
    }
    await onChanged(); setBusy("");
  };
  const remove = async (d) => {
    try { await tapi.delete(`/documents/${d.doc_id}`); await onChanged(); } catch (e) { toast.error(apiErr(e, "Couldn't remove file")); }
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      await tapi.post(`/t/${sub}/submit`);
      await onSubmitted();
    } catch (e) { toast.error(apiErr(e, "Couldn't submit your documents")); }
    setSubmitting(false);
  };

  const uploadedDocs = ECO_DOCS.filter((r) => filesFor(r).length > 0);
  const uploaded = uploadedDocs.length;
  return (
    <div className="space-y-5 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm" data-testid="tenant-step-eco-documents">
      <div>
        <h2 className="text-lg font-extrabold">Document submission</h2>
        <p className="mt-1 rounded-xl px-4 py-3 text-sm font-semibold" style={{ background: "color-mix(in srgb, var(--tenant) 8%, white)", color: "var(--tenant)" }} data-testid="eco-docs-message">
          Upload what you have — we will show you who we can connect you to based on your submission.
        </p>
        <p className="mt-2 text-xs text-neutral-500">All fields are optional. PDF, Excel, PowerPoint, Word or a clear photo all work.</p>
      </div>
      <ul className="space-y-3">{ECO_DOCS.map((r) => <DocRow key={r.key} req={r} files={filesFor(r)} onUpload={upload} onRemove={remove} busy={busy} optional />)}</ul>
      {submitting && <SubmitProgress docLabels={uploadedDocs.map((r) => r.label)} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        {onBack ? <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-xs font-semibold text-neutral-500 hover:text-neutral-800" data-testid="eco-docs-back"><ArrowLeft size={14} /> Back</button> : <span />}
        <div className="flex items-center gap-3">
          <span className="text-[11px] text-neutral-400" data-testid="eco-docs-count">{uploaded}/{ECO_DOCS.length} documents uploaded{uploaded === 0 ? " — you can still submit" : ""}</span>
          <BrandButton onClick={submit} disabled={!!busy || submitting} data-testid="eco-docs-submit">
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} {resubmit ? "Resubmit & update my results" : "Submit & see my results"} <ArrowRight size={16} />
          </BrandButton>
        </div>
      </div>
    </div>
  );
};
